import React, { forwardRef, InputHTMLAttributes } from 'react';
import { cn } from '../lib/utils';

export interface EnhancedInputProps extends InputHTMLAttributes<HTMLInputElement> {}

const EnhancedInput = forwardRef<HTMLInputElement, EnhancedInputProps>(
  ({ className, type, onFocus, onDoubleClick, onKeyDown, value, ...props }, ref) => {
    
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      // Auto-select content when focused
      e.target.select();
      onFocus?.(e);
    };

    const handleDoubleClick = (e: React.MouseEvent<HTMLInputElement>) => {
      // Select all content on double click
      e.currentTarget.select();
      onDoubleClick?.(e);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Handle Cmd+A (Mac) and Ctrl+A (PC) to select all
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault();
        e.currentTarget.select();
      }
      onKeyDown?.(e);
    };

    // Check if input has content (is filled)
    const isFilled = value !== undefined && value !== null && value !== '';

    return (
      <input
        type={type}
        className={cn(
          "flex h-10 w-full rounded-md border px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-colors",
          // Default state (empty)
          "border-[#969c96] bg-[rgba(244,245,244,0.5)] text-[#7d827d]",
          // Filled state
          isFilled && "border-[#0c0f0c] text-[#0c0f0c]",
          // Hover state
          "hover:border-[#0c0f0c]",
          className
        )}
        ref={ref}
        value={value}
        onFocus={handleFocus}
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleKeyDown}
        {...props}
      />
    );
  }
);

EnhancedInput.displayName = "EnhancedInput";

export { EnhancedInput };